import styled from 'styled-components'
import { resolveVisibility } from "../../../../common/model/condition/evaluate"
import useTranslation from '../../hooks/useTranslation'
import Modal from '../modal/Modal'
import ProgressTracker from '../generic/ProgressTracker'

const Header = styled.div`
    padding: 1rem 1rem 0.5rem;
    border-bottom: 1px solid ${({ theme }) => theme.modal.divider};
`

const Title = styled.div`
    font-size: 1rem;
    font-weight: 700;
    color: ${({ theme }) => theme.font.color};
    margin-bottom: 0.15rem;
`

const Subtitle = styled.div`
    font-size: 0.8rem;
    color: ${({ theme }) => theme.font.label};
`

const Body = styled.div`
    padding: 0.75rem 1rem;
`

const ProgressWrap = styled.div`
    margin-bottom: 0.75rem;
`

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${({ theme }) => theme.form.border};
    margin: 0.5rem 0;
`

const LineList = styled.div`
    max-height: 220px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    margin-bottom: 0.5rem;
`

const LineRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 0.83rem;
    color: ${({ theme, $done }) => $done ? theme.font.color : theme.font.label};
    padding: 0.2rem 0;
`

const LineStatus = styled.span`
    font-weight: 700;
    color: ${({ theme, $done }) => $done ? theme.btn.primary : theme.btn.danger};
    margin-right: 0.4rem;
    flex-shrink: 0;
`

const Warning = styled.div`
    background: ${({ theme }) => theme.area.highBg};
    border: 1px solid ${({ theme }) => theme.form.border};
    border-radius: 0.375rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.82rem;
    color: ${({ theme }) => theme.font.color};
    margin-top: 0.5rem;
`

const Footer = styled.div`
    padding: 0.75rem 1rem;
    display: flex;
    gap: 0.75rem;
    justify-content: space-between;
    border-top: 1px solid ${({ theme }) => theme.modal.divider};
`

const ActionButton = styled.button`
    padding: 0.4rem 1.2rem;
    font-size: 0.85rem;
    font-weight: 600;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    background: ${({ theme, $variant }) => $variant === 'secondary' ? theme.btn.secondary : theme.btn.primary};
    color: ${({ theme }) => theme.btn.font};
    &:hover { opacity: 0.88; }
`

const isButtonVisible = (btn, allDone) =>
    resolveVisibility(btn, { values: {}, context: { allDone } }) === 'show'

const ProcessCompletion = ({ doc, lines, doneField, section, onAction }) => {
    const { t, m } = useTranslation()

    const doneLines    = lines.filter(l =>  l[doneField])
    const pendingLines = lines.filter(l => !l[doneField])
    const allDone      = pendingLines.length === 0

    const buttons      = section?.buttons || []
    const leftButtons  = buttons.filter(b => isButtonVisible(b, allDone) && b.position === 'left')
    const rightButtons = buttons.filter(b => isButtonVisible(b, allDone) && b.position !== 'left')

    const titleSection = section.blocks?.find(s => s.type === 'title')
    const bodySections = (section.blocks || []).filter(s => s.type !== 'title')

    const subtitle = titleSection?.subtitle
        ?.map(f => doc[f.value] ? `${f.prefix ? f.prefix + ' ' : ''}${doc[f.value]}` : null)
        .filter(Boolean).join(' ') ?? null

    const renderLineElement = (el, line) => {
        if (el.type === 'field') {
            return <span key={el.value}>{line[el.value]}</span>
        }
        if (el.type === 'quantity' || el.type === 'quantitySplit') {
            const qty = el.type === 'quantitySplit'
                ? (line[el.actual] ?? line[el.planned])
                : line[el.value]
            const uom = el.decorator ? line[el.decorator] : ''
            return <span key="qty">{qty} {uom}</span>
        }
        return null
    }

    const renderLineRow = (s, line, done) => (
        <LineRow key={line.id} $done={done}>
            {s.columns.map((col, j) => (
                <span key={j}>
                    {j === 0 && <LineStatus $done={done}>{done ? '✓' : '✗'}</LineStatus>}
                    {(col.elements || []).map(el => renderLineElement(el, line))}
                </span>
            ))}
        </LineRow>
    )

    const renderBodySection = (s, i) => {
        if (s.type === 'progress') {
            return (
                <div key={i}>
                    <ProgressWrap>
                        <ProgressTracker value={doneLines.length} total={lines.length} size="md" />
                    </ProgressWrap>
                    <Divider />
                </div>
            )
        }
        if (s.type === 'lines') {
            return (
                <LineList key={i}>
                    {doneLines.map(l => renderLineRow(s, l, true))}
                    {pendingLines.map(l => renderLineRow(s, l, false))}
                </LineList>
            )
        }
        if (s.type === 'warning' && !allDone) {
            return (
                <Warning key={i}>
                    ⚠ {m(s.label, [pendingLines.length])}
                </Warning>
            )
        }
        return null
    }

    return (
        <>
            <Modal show onClose={() => onAction(section.closeEffects)} setShow={(v) => { if (!v) onAction(section.closeEffects) }}>
                {titleSection && (
                    <Header>
                        <Title>{t(titleSection.label)}</Title>
                        {subtitle && <Subtitle>{subtitle}</Subtitle>}
                    </Header>
                )}
                <Body>
                    {bodySections.map((s, i) => renderBodySection(s, i))}
                </Body>
                <Footer>
                    <div>{leftButtons.map((btn, i) => (
                        <ActionButton key={i} type="button" $variant={btn.variant} data-testid={btn.id} onClick={() => onAction(btn.effects)}>
                            {t(btn.label)}
                        </ActionButton>
                    ))}</div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {rightButtons.map((btn, i) => (
                            <ActionButton key={i} type="button" $variant={btn.variant} data-testid={btn.id} onClick={() => onAction(btn.effects)}>
                                {t(btn.label)}
                            </ActionButton>
                        ))}
                    </div>
                </Footer>
            </Modal>
        </>
    )
}

export default ProcessCompletion
